import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, Lock, Settings } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { PREMIUM_BENEFITS as PERKS, BONUS_ONLY_NOTE } from "@/lib/siteContent";

export default function Premium() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [price, setPrice] = useState(2.99);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/game/zones").then(({ data }) => setPrice(data.premium_price || 2.99)).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <p className="font-mono-caps text-xs text-primary mb-4">Membership</p>
        <h1 className="font-display text-5xl sm:text-6xl mb-4">GO <span className="text-primary">PREMIUM</span></h1>
        <p className="text-muted-foreground max-w-xl mb-10">New Jack stays free. Premium opens regular New Jack and Triple OG puzzles, including the Archive.</p>

        {user?.is_premium ? (
          <div className="border-2 border-primary bg-card p-8 flex flex-wrap items-center justify-between gap-4" data-testid="premium-active">
            <div className="flex items-center gap-4">
              <Crown className="text-primary" size={32} />
              <div>
                <h2 className="font-display text-2xl">You're Premium</h2>
                <p className="text-muted-foreground text-sm">All regular New Jack and Triple OG puzzles unlocked. Enjoy the drop.</p>
              </div>
            </div>
            <button onClick={openPortal} data-testid="premium-manage-btn" className="flex items-center gap-2 bg-foreground text-background font-mono-caps text-xs px-5 py-3 hover-lift border border-foreground">
              <Settings size={15} /> Manage Subscription
            </button>
          </div>
        ) : (
          <div className="relative border-2 border-primary bg-card p-8 brutal-shadow">
            <div className="absolute -top-3 left-6 bg-primary text-primary-foreground font-mono-caps text-[9px] px-3 py-1">Most Popular</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-display text-6xl">${price.toFixed(2)}</span>
              <span className="font-mono-caps text-xs text-muted-foreground">/ month</span>
            </div>
            <p className="font-mono-caps text-[10px] text-secondary mb-6">Cancel anytime</p>
            <ul className="space-y-3 mb-8" data-testid="premium-benefits">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm"><Check size={18} className="text-primary shrink-0 mt-0.5" /> {p}</li>
              ))}
            </ul>
            <button onClick={subscribe} disabled={busy} data-testid="subscribe-btn"
              className="w-full bg-primary text-primary-foreground font-mono-caps text-xs py-4 border border-primary hover-lift disabled:opacity-50 flex items-center justify-center gap-2">
              <Crown size={15} /> {busy ? "Redirecting to Stripe…" : "Subscribe with Stripe"}
            </button>
          </div>
        )}
        {user?.is_premium && <ul className="space-y-3 mt-8" data-testid="premium-benefits">
          {PERKS.map(p => <li key={p} className="flex items-start gap-3 text-sm"><Check size={18} className="text-primary shrink-0 mt-0.5" /> {p}</li>)}
        </ul>}
        <p className="mt-6 text-muted-foreground text-sm leading-relaxed">{BONUS_ONLY_NOTE}</p>
      </div>
    </Layout>
  );
}
